import {OfficeWorkday, ReservationCount, WorkspaceReservation} from '../../../data/timeslots/apiTypes';
import {useAppSelector} from '../../../hooks';
import {useWorkplaceReservationCounts} from './useWorkplaceAvailability';

interface Args {
  workday: OfficeWorkday;
  workspaceReservation?: WorkspaceReservation;
  buildingId: string;
  floorId?: string;
  areaId?: string;
  inviteeCount?: number;
}

/**
 * Determines if the workspace has enough space for a group booking
 * based on the reservations and node capacity, using the current invitee count.
 * Excludes the current user's booking from the invitee count.
 */
export const useWorkspaceHasSpaceForGroupBooking = ({
  workday,
  workspaceReservation,
  buildingId,
  floorId,
  areaId,
  inviteeCount = 0,
}: Args) => {
  const reservationCounts = useWorkplaceReservationCounts(workday);
  const nodeAvailability = getNodeAvailability(reservationCounts, buildingId, floorId, areaId);
  const invitees = workspaceReservation ? inviteeCount - 1 : inviteeCount; // Exclude current user's booking

  const nodeCapacity = useAppSelector((state) => {
    const buildings = state.data.buildings.denormalized;

    const building = buildings.find((b) => b.id === buildingId);
    if (!building) return 0;

    const floor = floorId ? building.floors.find((f) => f.id === floorId) : undefined;
    if (!floor) return building.capacity;

    const area = areaId ? floor.areas.find((a) => a.id === areaId) : undefined;
    if (!area) return floor.capacity;

    return area.capacity;
  });

  const locationHasSpace = !!reservationCounts && nodeCapacity - nodeAvailability.booked >= invitees;

  return locationHasSpace;
};

const getNodeAvailability = (
  reservationCounts: Record<string, ReservationCount | undefined> | null,
  buildingId?: string,
  floorId?: string,
  areaId?: string,
): {booked: number} => {
  if (reservationCounts === null) return {booked: 0};

  if (areaId && areaId !== '' && reservationCounts[areaId]) {
    return reservationCounts[areaId]!;
  }

  if (floorId && floorId !== '' && reservationCounts[floorId]) {
    return reservationCounts[floorId]!;
  }

  if (buildingId && buildingId !== '' && reservationCounts[buildingId]) {
    return reservationCounts[buildingId]!;
  }

  return {booked: 0};
};
